// src/components/ProjectBuilder/TaskFilters.tsx
import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Typography,
} from '@mui/material';
import { Clear, FilterList, ExpandMore, ExpandLess } from '@mui/icons-material';
import { TaskFilters as TaskFiltersType } from '../../services/taskApi';
import AirportManagerFilters from './AirportManagerFilters';

export interface TaskFiltersProps {
  filters: TaskFiltersType;
  onFilterChange: (filters: TaskFiltersType) => void;
  userRole: 'System Admin' | 'Airport Manager' | 'Fraport User' | 'Tenant User (365 Tenant Sense)' | 'Client User';
}

export default function TaskFilters({ filters, onFilterChange, userRole }: TaskFiltersProps) {
  const [expanded, setExpanded] = useState(false);
  const isManagerOrAdmin = userRole === 'System Admin' || userRole === 'Airport Manager';

  const handleFilterChange = (key: keyof TaskFiltersType, value: string | number | boolean | undefined) => {
    onFilterChange({
      ...filters,
      [key]: value,
      page: 1, // Reset to first page on filter change
    });
  };

  const handleClearFilters = () => {
    onFilterChange({
      page: 1,
      pageSize: filters.pageSize || 50,
    });
  };

  const hasActiveFilters = !!(
    filters.startDate ||
    filters.endDate ||
    filters.projectId ||
    filters.airportId ||
    filters.userId ||
    filters.userType ||
    filters.status ||
    filters.pastDueOnly ||
    filters.upcomingDays
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterList />
          <Button
            variant="text"
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          >
            Filters
          </Button>
          {hasActiveFilters && (
            <Chip
              label="Active"
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<Clear />}
            onClick={handleClearFilters}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          {/* Date Range */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={filters.startDate || ''}
              onChange={(e) => handleFilterChange('startDate', e.target.value || undefined)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={filters.endDate || ''}
              onChange={(e) => handleFilterChange('endDate', e.target.value || undefined)}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
          </Box>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status || ''}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="NOT_STARTED">Not Started</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="COMPLETE">Complete</MenuItem>
            </Select>
          </FormControl>

          {/* Quick Filters */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick Filters
            </Typography>
            <ToggleButtonGroup
              value={filters.pastDueOnly ? 'pastDue' : filters.upcomingDays ? `upcoming${filters.upcomingDays}` : ''}
              exclusive
              onChange={(_, value) => {
                if (value === 'pastDue') {
                  handleFilterChange('pastDueOnly', true);
                  handleFilterChange('upcomingDays', undefined);
                } else if (value?.startsWith('upcoming')) {
                  const days = parseInt(value.replace('upcoming', ''));
                  handleFilterChange('upcomingDays', days);
                  handleFilterChange('pastDueOnly', undefined);
                } else {
                  handleFilterChange('pastDueOnly', undefined);
                  handleFilterChange('upcomingDays', undefined);
                }
              }}
              size="small"
            >
              <ToggleButton value="pastDue">Past Due Only</ToggleButton>
              <ToggleButton value="upcoming7">Upcoming 7d</ToggleButton>
              <ToggleButton value="upcoming14">Upcoming 14d</ToggleButton>
              <ToggleButton value="upcoming30">Upcoming 30d</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Manager/Admin Only Filters */}
          {isManagerOrAdmin && (
            <>
              <AirportManagerFilters
                filters={filters}
                onFilterChange={onFilterChange}
              />
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

